import type { ReactNode } from 'react'
import React from 'react'
import type { ToastOptions, TypeOptions } from 'react-toastify'
import { toast } from 'react-toastify'

import type { IToast } from '@/interfaces'

const activeToasts = new Set<string>()

const getToastContent = (type: TypeOptions, msg: string): ReactNode => {
  const headers = {
    info: 'Info',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    default: '',
  }

  const header = headers[type as keyof typeof headers] || ''

  return React.createElement('div', null, [
    header &&
      React.createElement(
        'div',
        {
          key: 'header',
          style: {
            fontSize: '1.6rem',
            fontWeight: 'bold',
            marginBottom: '1px',
          },
        },
        header,
      ),
    React.createElement(
      'div',
      {
        key: 'message',
        style: {
          fontSize: '1.3rem',
          fontWeight: 500,
        },
      },
      msg,
    ),
  ])
}

const persistentToast = (msg: string, type: TypeOptions) => {
  let toastId: string | number

  const show = () => {
    toast.dismiss()
    toastId = toast(getToastContent(type, msg), {
      type,
      autoClose: false,
      icon: false,
    })
  }

  const hide = () => {
    toast.dismiss(toastId)
  }

  return { show, hide }
}

const customToast: IToast = (msg, type, position, promise, duration = 4000) => {
  if (activeToasts.has(msg)) {
    return
  }

  activeToasts.add(msg)

  const removeToast = () => {
    setTimeout(() => {
      activeToasts.delete(msg)
    }, duration)
  }

  if (type === 'promise' && promise) {
    toast.promise(
      promise,
      {
        pending: 'Loading',
        success: 'Got the data',
        error: 'Error when fetching',
      },
      {
        icon: false,
      },
    )
  } else {
    const toastType: TypeOptions = type === 'custom' ? 'default' : (type as TypeOptions)

    toast(getToastContent(toastType, msg), {
      position,
      type: toastType,
      autoClose: duration,
      icon: false,
    } as ToastOptions)

    if (type !== 'promise') {
      removeToast()
    }
  }
}

const customToastTop = (
  msg: string,
  type: 'success' | 'error' | 'info' | 'warning' | 'custom' | 'promise' = 'info',
  duration = 4000,
) => {
  return customToast(msg, type, 'top-right', undefined, duration)
}

const useToast = () => {
  return { customToast, customToastTop, persistentToast }
}

export { customToast, customToastTop, persistentToast, useToast }
